import React, { useState, useEffect } from 'react';
import {
  Menu,
  MenuItem,
  TextField,
  Box,
  Avatar,
  Typography,
  InputAdornment,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { LeadDTO } from '../../api/generated/requests/types.gen';
import { LeadSearchService, LeadCrudService } from '../../api/generated/requests/services.gen';
import { useQuery } from '@tanstack/react-query';

interface ContactSearchMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelectContact: (leadId: number | undefined) => void;
  currentContactId?: number;
  onAddNewContact?: () => void;
}

const ContactSearchMenu: React.FC<ContactSearchMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onSelectContact,
  currentContactId,
  onAddNewContact,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showMinCharWarning, setShowMinCharWarning] = useState(false);

  // Search query - only trigger when searchTerm has 3+ characters
  const shouldSearch = searchTerm.trim().length >= 3;
  
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['lead-contacts-search', searchTerm.trim()],
    queryFn: () => LeadSearchService.getApiLeadContactsSearch({ q: searchTerm.trim() }),
    enabled: shouldSearch,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Default paginated query - when no search or search term < 3 chars
  const { data: defaultResults, isLoading: isLoadingDefault } = useQuery({
    queryKey: ['lead-contacts-paginated-default'],
    queryFn: () => LeadCrudService.getApiLeadContactsPaginated({
      page: 0,
      size: 40,
      sortBy: 'CREATED_AT',
      sortDirection: 'DESC'
    }),
    enabled: !shouldSearch,
    staleTime: 60000, // Cache for 1 minute
  });

  // Determine which data to use
  const leads = shouldSearch ? (searchResults || []) : (defaultResults?.content || []);
  const isLoading = shouldSearch ? isSearching : isLoadingDefault;

  // Handle search term changes
  useEffect(() => {
    if (searchTerm.trim().length > 0 && searchTerm.trim().length < 3) {
      setShowMinCharWarning(true);
    } else {
      setShowMinCharWarning(false);
    }
  }, [searchTerm]);

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  const handleSelectContact = (leadId: number | undefined) => {
    onSelectContact(leadId);
    handleClose();
  };

  const handleAddNewContact = () => {
    if (onAddNewContact) {
      onAddNewContact();
    }
    // Don't close the menu immediately - let the parent component handle closing
    // after the contact is successfully created and assigned
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        style: {
          maxHeight: 400,
          minWidth: 300,
        },
      }}
    >
      {/* Search field */}
      <Box sx={{ p: 1, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Szukaj kontaktu..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </Box>

      <Divider />

      {/* Minimum character warning */}
      {showMinCharWarning && (
        <>
          <Box sx={{ p: 1 }}>
            <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
              Wpisz co najmniej 3 znaki aby wyszukać kontakty
            </Alert>
          </Box>
          <Divider />
        </>
      )}

      {/* Add new contact option */}
      {onAddNewContact && (
        <>
          <MenuItem onClick={handleAddNewContact}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main', fontSize: '0.75rem' }}>
                <AddIcon fontSize="small" />
              </Avatar>
              <Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>Dodaj nowy kontakt</Typography>
            </Box>
          </MenuItem>
          <Divider />
        </>
      )}

      {/* No assignment option */}
      <MenuItem 
        onClick={() => handleSelectContact(undefined)}
        sx={{
          backgroundColor: !currentContactId ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.300', fontSize: '0.75rem' }}>
            <BusinessIcon fontSize="small" />
          </Avatar>
          <Typography sx={{ fontSize: '0.8rem' }}>Brak przypisania do kontaktu</Typography>
          {!currentContactId && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
        </Box>
      </MenuItem>

      {/* Loading state */}
      {isLoading && (
        <MenuItem disabled>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: '0.8rem' }}>Ładowanie kontaktów...</Typography>
          </Box>
        </MenuItem>
      )}

      {/* No results */}
      {!isLoading && !showMinCharWarning && leads.length === 0 && (
        <MenuItem disabled>
          <Typography sx={{ fontSize: '0.8rem' }} color="text.secondary">
            {shouldSearch ? `Brak wyników dla "${searchTerm}"` : 'Brak kontaktów'}
          </Typography>
        </MenuItem>
      )}

      {/* Contacts list */}
      {!isLoading && !showMinCharWarning && leads.map((lead) => {
        const isCurrentLead = currentContactId === lead.id;
        
        // Create display name directly from lead object data
        const getDisplayName = (lead: LeadDTO) => {
          if (lead.firstName && lead.lastName) {
            return `${lead.firstName} ${lead.lastName}`;
          } else if (lead.name) {
            return lead.name;
          } else if (lead.firstName) {
            return lead.firstName;
          } else if (lead.lastName) {
            return lead.lastName;
          } else if (lead.email) {
            return lead.email;
          } else {
            return `Contact ${lead.id}`;
          }
        };
        
        return (
          <MenuItem 
            key={lead.id} 
            onClick={() => handleSelectContact(lead.id)}
            sx={{
              backgroundColor: isCurrentLead ? 'rgba(0, 0, 0, 0.08)' : 'inherit',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main', fontSize: '0.75rem' }}>
                <BusinessIcon fontSize="small" />
              </Avatar>
              <Typography sx={{ fontSize: '0.8rem' }}>{getDisplayName(lead)}</Typography>
              {isCurrentLead && <CheckIcon fontSize="small" sx={{ ml: 1 }} />}
            </Box>
          </MenuItem>
        );
      })}
    </Menu>
  );
};

export default ContactSearchMenu;
